import React, { useState, useEffect } from 'react';
import Icon from '../Common/Icon';
import './AuthForms.css';

/**
 * ResetPassword Component
 *
 * Allows users to set a new password after clicking reset link from email
 * - New password + confirm password fields
 * - Password strength validation
 * - Success/error handling
 * - Redirect to login after success
 */
const ResetPassword = ({ onUpdatePassword, onGoToLogin, isLoading = false }) => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, message: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Check if we have a valid reset token in the URL
  useEffect(() => {
    // Supabase will handle the token from URL automatically via detectSessionInUrl
    // We just need to verify the user has a session after the redirect
  }, []);

  // Password strength calculator (same as SignupForm)
  const calculatePasswordStrength = (password) => {
    if (!password) return { score: 0, message: '' };

    let score = 0;
    const feedback = [];

    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    else feedback.push('at least 12 characters');

    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) {
      score++;
    } else {
      feedback.push('uppercase and lowercase letters');
    }

    if (/\d/.test(password)) {
      score++;
    } else {
      feedback.push('numbers');
    }

    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      score++;
    } else {
      feedback.push('special characters');
    }

    const commonPatterns = ['password', '12345', 'qwerty', 'abc123'];
    if (commonPatterns.some(pattern => password.toLowerCase().includes(pattern))) {
      score = Math.max(0, score - 2);
      feedback.push('avoid common patterns');
    }

    let message = '';
    if (score <= 1) message = 'Weak';
    else if (score === 2) message = 'Fair';
    else if (score === 3) message = 'Good';
    else if (score === 4) message = 'Strong';
    else message = 'Excellent';

    if (feedback.length > 0 && score < 4) {
      message += ` - Add ${feedback.join(', ')}`;
    }

    return { score, message };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    setValidationErrors(prev => ({
      ...prev,
      [name]: null,
    }));

    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    setError(null);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (passwordStrength.score < 2) {
      errors.password = 'Please choose a stronger password';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const result = await onUpdatePassword(formData.password);

      if (result?.error) {
        setError(result.error);
      } else {
        setIsSubmitted(true);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          onGoToLogin();
        }, 3000);
      }
    } catch (err) {
      setError('Failed to reset password. Please try again or request a new reset link.');
    }
  };

  const getStrengthColor = (score) => {
    if (score <= 1) return '#ef4444';
    if (score === 2) return '#f59e0b';
    if (score === 3) return '#3b82f6';
    return '#10b981';
  };

  // Success view
  if (isSubmitted) {
    return (
      <div className="auth-form-container">
        <div className="auth-form">
          <div className="auth-form-header">
            <h2>Password Reset Successful!</h2>
            <p className="auth-form-subtitle">
              Your password has been updated successfully.
            </p>
          </div>

          <div className="auth-success-banner">
            <p>
              <strong>All set!</strong> You can now log in with your new password.
            </p>
            <p style={{ marginTop: '0.75rem' }}>
              Redirecting you to login...
            </p>
          </div>

          <button
            className="btn-modern primary"
            onClick={onGoToLogin}
            style={{ width: '100%' }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="auth-form-container">
      <form className="auth-form" onSubmit={handleSubmit}>
        <div className="auth-form-header">
          <h2>Create New Password</h2>
          <p className="auth-form-subtitle">
            Enter your new password below. Make sure it's strong and secure.
          </p>
        </div>

        {error && (
          <div className="auth-error-banner" role="alert">
            {error}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="password">
            New Password <span className="required-label">*</span>
          </label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a strong password"
              autoComplete="new-password"
              required
              disabled={isLoading}
              className={validationErrors.password ? 'input-error' : ''}
              autoFocus
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isLoading}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name={showPassword ? 'eye-off' : 'eye'} size={18} strokeWidth={2.2} />
            </button>
          </div>
          {formData.password && (
            <div className="password-strength">
              <div className="password-strength-bar">
                <div
                  className="password-strength-fill"
                  style={{
                    width: `${(passwordStrength.score / 5) * 100}%`,
                    backgroundColor: getStrengthColor(passwordStrength.score),
                  }}
                />
              </div>
              <span
                className="password-strength-text"
                style={{ color: getStrengthColor(passwordStrength.score) }}
              >
                {passwordStrength.message}
              </span>
            </div>
          )}
          {validationErrors.password && (
            <span className="error-message">{validationErrors.password}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">
            Confirm New Password <span className="required-label">*</span>
          </label>
          <input
            type={showPassword ? 'text' : 'password'}
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Confirm your new password"
            autoComplete="new-password"
            required
            disabled={isLoading}
            className={validationErrors.confirmPassword ? 'input-error' : ''}
          />
          {validationErrors.confirmPassword && (
            <span className="error-message">{validationErrors.confirmPassword}</span>
          )}
        </div>

        <button
          type="submit"
          className="btn-modern primary auth-submit-btn"
          disabled={isLoading}
        >
          {isLoading ? 'Updating Password...' : 'Reset Password'}
        </button>

        <div className="auth-form-footer">
          <p>
            <button
              type="button"
              className="link-button"
              onClick={onGoToLogin}
              disabled={isLoading}
            >
              Back to Login
            </button>
          </p>
        </div>

        <div className="privacy-notice">
          <p>
            <strong>Security Tip:</strong> Choose a unique password that you don't use
            for other accounts. We recommend using a password manager.
          </p>
        </div>
      </form>
    </div>
  );
};

export default ResetPassword;
